import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, RotateCcw, User, Bot, Cpu, Store, FlaskConical, Check } from "lucide-react";
import site from "../content/site.json";
import { btnPrimary } from "../components/CtaLink";

const ACTOR = {
  customer: { label: "Customer", icon: User, cls: "bg-white border-slate-200 text-slate-800 ml-auto" },
  assistant: { label: "AI assistant", icon: Bot, cls: "bg-indigo-600 border-indigo-600 text-white" },
  agent: { label: "Agent action", icon: Cpu, cls: "bg-slate-900 border-slate-800 text-indigo-200 font-mono text-[13px]" },
  business: { label: "Business receives", icon: Store, cls: "bg-emerald-50 border-emerald-300 text-emerald-900 font-semibold" },
};

function Simulation({ demo }) {
  const [shown, setShown] = useState(0);
  const timer = useRef(null);
  const stop = () => clearInterval(timer.current);
  useEffect(() => { setShown(0); stop(); return stop; }, [demo.id]);
  const play = () => {
    stop(); setShown(0);
    timer.current = setInterval(() => setShown((n) => { if (n + 1 >= demo.steps.length) stop(); return n + 1; }), 1100);
  };
  const done = shown >= demo.steps.length;
  return (
    <div className="grid lg:grid-cols-[1fr_300px] gap-8" data-testid={`demo-${demo.id}`}>
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 min-h-[420px] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-semibold text-slate-700">{demo.business}</p>
          <button onClick={play} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold whitespace-nowrap shrink-0" data-testid={`demo-play-${demo.id}`}>
            {done ? <><RotateCcw className="h-4 w-4" />Replay</> : shown > 0 ? "Playing…" : <><Play className="h-4 w-4" />Play<span className="hidden sm:inline"> demonstration</span></>}
          </button>
        </div>
        {shown === 0 && <p className="m-auto text-sm text-slate-400">Press play to watch the simulation step by step.</p>}
        <div className="space-y-3">
          {demo.steps.slice(0, shown).map(([who, text], i) => {
            const a = ACTOR[who];
            const Icon = a.icon;
            return (
              <div key={i} className={`max-w-[85%] rounded-xl border px-4 py-3 text-sm animate-fade-up ${a.cls}`} data-testid={`demo-step-${demo.id}-${i}`}>
                <p className="text-[10px] uppercase tracking-wide opacity-70 flex items-center gap-1 mb-1"><Icon className="h-3 w-3" />{a.label}</p>
                {text}
              </div>
            );
          })}
        </div>
      </div>
      <div>
        <p className="font-heading font-bold text-slate-900">What has to be in place</p>
        <ul className="mt-3 space-y-2">{demo.needs.map((n) => <li key={n} className="flex gap-2 text-sm text-slate-600"><Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />{n}</li>)}</ul>
        <p className="text-xs text-slate-500 mt-5">Simulated data. Real completion depends on the AI platform and your integrations.</p>
      </div>
    </div>
  );
}

export default function Demos() {
  const [active, setActive] = useState(site.demos[0].id);
  const demo = site.demos.find((d) => d.id === active);
  return (
    <div>
      <div className="bg-amber-400 text-slate-950 text-center text-sm font-semibold py-2 px-4" data-testid="demo-banner"><FlaskConical className="inline h-4 w-4 mr-1.5 -mt-0.5" />DEMONSTRATION — simulated, not a client result</div>
      <section className="bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">See an AI agent do business with you.</h1>
          <p className="mt-4 text-slate-300 max-w-3xl" data-testid="demo-intro">{site.demos_intro}</p>
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="demo-tabs">
          {site.demos.map((d) => (
            <button key={d.id} onClick={() => setActive(d.id)} data-testid={`demo-tab-${d.id}`}
              className={`h-10 px-5 rounded-full text-sm font-semibold border transition-colors ${active === d.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{d.title}</button>
          ))}
        </div>
        <Simulation demo={demo} />
        <div className="mt-12 flex flex-wrap gap-3">
          <Link to="/free-scan" className={btnPrimary} data-testid="demo-cta-scan">See if agents can read my business</Link>
          <Link to="/ai-visibility-vs-ai-transactions" className="inline-flex items-center h-12 px-6 rounded-full border border-slate-300 bg-white text-slate-800 font-semibold text-sm" data-testid="demo-cta-visibility">AI visibility vs. AI transactions</Link>
        </div>
      </section>
    </div>
  );
}
